import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import kgDatasetService from '../../services/kgDatasetService';
import { unwrapList as unwrapKgList } from '../KnowledgeGraph/kgUtils';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import filesD3Service from '../../services/filesD3Service';
import folderD3Service from '../../services/folderD3Service';
import PageHeader from '../shared/PageHeader';
import PageLoader from '../shared/PageLoader';
import {
  Typography,
  Grid,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Chip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  RefreshCw,
  Filter,
  Search,
  Eye,
  Trash2,
  Upload,
  FolderOpen,
  Folder,
  ArrowLeft,
  ChevronRight,
  Cloud,
  Info,
  Network,
} from 'lucide-react';
import viewStyles from '../KnowledgeBaseView/KnowledgeBaseView.module.scss';
import hubStyles from '../KnowledgeBaseHub/KnowledgeBaseHub.module.scss';
import FileDeleteDialog from './FileDeleteDialog';
import FolderDeleteDialog from './FolderDeleteDialog';
import ResourceShareDialog from '../shared/ResourceShareDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';
import { getChildFolders, getFolderPath, countDescendantFolders, isMirroredFolder } from '../../utils/folderTree';
import GoogleDriveSyncDialog from '../AgentTriggers/GoogleDriveSyncDialog';

const FolderFilesViewD3 = () => {
  const { isDarkMode } = useTheme();
  const { has, ready: permissionsReady } = usePermissions();
  const canShareResources = permissionsReady && has(PERMISSIONS.RESOURCE_SHARING);
  const canManageTriggers = permissionsReady && has(PERMISSIONS.TRIGGERS);
  const { folderId } = useParams();
  useEffect(() => {
    let cancelled = false;
    kgDatasetService
      .listDatasets()
      .then((response) => {
        if (cancelled) return;
        const match = unwrapKgList(response, 'datasets', 'kg_datasets').find(
          (d) => String(d?.folder_id) === String(folderId)
        );
        setKgDataset(match || null);
      })
      // No permission, or the feature is off — behave as an ordinary folder.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [folderId]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isShared = searchParams.get('shared') === 'true';
  const canModifyFiles = !isShared || searchParams.get('permission') === 'write';

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250 !important',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [folder, setFolder] = useState(null);
  // A folder owned by a Knowledge Graph dataset holds pushed records, not files.
  // Every affordance on this page — Upload, Sync, the file list — is wrong for
  // it, and the list is permanently empty because records are chunked straight
  // into the vector store. So this page refuses to pretend.
  const [kgDataset, setKgDataset] = useState(null);
  // Full flat folder list — used to derive this folder's subfolders + breadcrumb path.
  const [allFolders, setAllFolders] = useState([]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedFile, setSelectedFile] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [manageSharingOpen, setManageSharingOpen] = useState(false);
  const [driveSyncOpen, setDriveSyncOpen] = useState(false);

  const [folderDeleteDialog, setFolderDeleteDialog] = useState({
    open: false,
    folder: null,
    subfolderCount: 0,
    isDeleting: false,
  });

  const [stats, setStats] = useState({
    total_files: 0,
    pending_files: 0,
    processing_files: 0,
    completed_files: 0,
    failed_files: 0,
    total_filters: 0,
  });

  const loadData = useCallback(
    /** @param {{ silent?: boolean }} [opts] */
    async (opts = {}) => {
      const silent = !!opts.silent;
      if (!silent) setLoading(true);
      try {
        const [folderWithFiles, foldersList] = await Promise.all([
          folderD3Service.getFolderWithFiles(folderId),
          folderD3Service.getFolders().catch(() => []),
        ]);

        const filesList = folderWithFiles.files || [];
        const folderData = folderWithFiles.folder || {};

        setFolder(folderData);
        setFiles(filesList);
        setAllFolders(Array.isArray(foldersList) ? foldersList : []);

        setStats({
          total_files: folderWithFiles.file_count || filesList.length,
          pending_files: filesList.filter((f) => f.processing_status === 'pending').length,
          processing_files: filesList.filter((f) => f.processing_status === 'processing').length,
          completed_files: filesList.filter((f) => f.processing_status === 'completed').length,
          failed_files: filesList.filter((f) => f.processing_status === 'failed').length,
          total_filters: 0,
        });
      } catch (error) {
        console.error('Error loading D3 data:', error);

        if (!silent) {
          setFiles([]);
          setStats({
            total_files: 0,
            pending_files: 0,
            processing_files: 0,
            completed_files: 0,
            failed_files: 0,
            total_filters: 0,
          });

          if (error.message.includes('Failed to fetch')) {
            showToast('API service not available. Showing empty state.', 'warning');
          } else {
            showToast('Failed to load D3 data', 'error');
          }
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [folderId]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const hasProcessing = files.some((f) => f.processing_status === 'processing');
    if (!hasProcessing || !folderId) return undefined;

    const id = window.setInterval(() => {
      loadData({ silent: true });
    }, 20000);

    return () => clearInterval(id);
  }, [files, folderId, loadData]);

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleToastClose = () => {
    setToast({ open: false, message: '', severity: 'info' });
  };

  const filteredFiles = files.filter((file) => {
    const processingStatus = file.processing_status;
    const fileName = file.file_name;

    const matchesStatus = statusFilter === 'all' || processingStatus === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === '' ||
      (fileName && String(fileName).toLowerCase().includes(q));

    return matchesStatus && matchesSearch;
  });

  const getStatusIcon = (status) => {
    const iconProps = { size: 20, strokeWidth: 2 };
    switch (status) {
      case 'completed':
        return <CheckCircle2 {...iconProps} color="#16a34a" aria-hidden />;
      case 'failed':
        return <XCircle {...iconProps} color="#dc2626" aria-hidden />;
      case 'processing':
        return <Loader2 {...iconProps} color="#ca8a04" className={viewStyles.spin} aria-hidden />;
      case 'pending':
        return <Clock {...iconProps} color="#6b7280" aria-hidden />;
      default:
        return <Info {...iconProps} color="#3b82f6" aria-hidden />;
    }
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'completed':
        return hubStyles.folderChipOk;
      case 'failed':
        return viewStyles.filesStatDangerChip;
      case 'processing':
        return viewStyles.filesStatWarnChip;
      case 'pending':
        return hubStyles.folderChip;
      default:
        return hubStyles.folderChipAccent;
    }
  };

  const handleViewDetails = (file) => {
    setSelectedFile(file);
    setDetailsDialogOpen(true);
  };

  const handleRetryFile = async (fileId) => {
    try {
      await filesD3Service.updateFileStatus(fileId, 'pending');
      showToast('D3 File queued for retry', 'success');
      loadData();
    } catch (error) {
      showToast('Failed to retry D3 file', 'error');
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    
    setIsDeleting(true);
    try {
      await filesD3Service.deleteFile(fileToDelete.id);
      showToast('D3 File deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      loadData();
    } catch (error) {
      showToast('Failed to delete D3 file', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (sizeString) => {
    if (!sizeString) return 'N/A';
    const bytes = parseInt(sizeString, 10);
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUploadFiles = () => {
    navigate(`/knowledge-base-d3/folder/${folderId}/upload`);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
  };

  const uploadAction = canModifyFiles ? (
    <button type="button" className={hubStyles.uploadBtn} onClick={handleUploadFiles}>
      <Upload size={18} strokeWidth={2} aria-hidden />
      Upload New Files
    </button>
  ) : null;
  const driveSyncAction =
    canModifyFiles && canManageTriggers ? (
      <button
        type="button"
        className={hubStyles.uploadBtn}
        onClick={() => setDriveSyncOpen(true)}
      >
        <Cloud size={18} strokeWidth={2} aria-hidden />
        Sync files
      </button>
    ) : null;
  const headerActions = (
    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {uploadAction}
      {driveSyncAction}
      {!isShared && canShareResources && <Button size="small" variant="outlined" onClick={() => setShareOpen(true)}>Share</Button>}
      {!isShared && canShareResources && <Button size="small" variant="outlined" onClick={() => setManageSharingOpen(true)}>Manage sharing</Button>}
    </span>
  );

  const statItems = [
    { label: 'Total', value: stats.total_files, color: '#3b82f6' },
    { label: 'Pending', value: stats.pending_files, color: '#6b7280' },
    { label: 'Processing', value: stats.processing_files, color: '#ca8a04' },
    { label: 'Completed', value: stats.completed_files, color: '#16a34a' },
    { label: 'Failed', value: stats.failed_files, color: '#dc2626' },
    { label: 'Configurations', value: stats.total_filters, color: '#8b5cf6' },
  ];

  const paginatedFiles = filteredFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Nested-folder navigation derived from the flat folder list.
  const subfolders = getChildFolders(allFolders, folderId);
  const folderPath = getFolderPath(allFolders, folderId);
  const parentFolder = folderPath.length > 1 ? folderPath[folderPath.length - 2] : null;
  const currentNode = folderPath.length ? folderPath[folderPath.length - 1] : null;
  const currentMirrored = isMirroredFolder(currentNode) || isMirroredFolder(folder);

  const handleBack = () => {
    if (parentFolder) {
      navigate(`/knowledge-base-d3/folder/${parentFolder.id}`);
    } else {
      navigate('/knowledge-hub?tab=d3');
    }
  };

  const openSubfolder = (subfolder) => {
    navigate(`/knowledge-base-d3/folder/${subfolder.id}`);
  };

  const handleDeleteSubfolder = (sub) => {
    setFolderDeleteDialog({
      open: true,
      folder: sub,
      subfolderCount: countDescendantFolders(allFolders, sub.id),
      isDeleting: false,
    });
  };

  const handleConfirmDeleteSubfolder = async () => {
    const target = folderDeleteDialog.folder;
    if (!target) return;
    setFolderDeleteDialog((prev) => ({ ...prev, isDeleting: true }));
    try {
      await folderD3Service.deleteFolder(target.id);
      showToast(`Folder "${target.name}" deleted successfully`, 'success');
      setFolderDeleteDialog({ open: false, folder: null, subfolderCount: 0, isDeleting: false });
      loadData();
    } catch (error) {
      console.error('Error deleting subfolder:', error);
      showToast('Failed to delete folder. Please try again.', 'error');
      setFolderDeleteDialog((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const handleCancelDeleteSubfolder = () => {
    setFolderDeleteDialog({ open: false, folder: null, subfolderCount: 0, isDeleting: false });
  };

  const renderSubfolderRow = (sub) => {
    const childCount = getChildFolders(allFolders, sub.id).length;
    return (
      <div
        key={`folder-${sub.id}`}
        className={hubStyles.fileRow}
        style={{ cursor: 'pointer' }}
        onClick={() => openSubfolder(sub)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openSubfolder(sub);
        }}
      >
        <div className={hubStyles.fileRowMain}>
          <div className={`${hubStyles.typeIconWrap} ${hubStyles.typeDoc}`}>
            <Folder size={20} strokeWidth={2} color="#8b5cf6" aria-hidden />
          </div>
          <div className={hubStyles.fileMeta}>
            <h3>{sub.name}</h3>
            <div className="subRow">
              {isMirroredFolder(sub) ? (
                <span style={{ color: '#8b5cf6' }}>Synced from Google Drive</span>
              ) : (
                <span>Created {new Date(sub.created_at).toLocaleDateString()}</span>
              )}
              {childCount > 0 && (
                <>
                  <span>·</span>
                  <span>{childCount} subfolder{childCount === 1 ? '' : 's'}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={`${hubStyles.rowActions} ${viewStyles.fileFolderRowActions}`}>
          {!isShared && <Tooltip title="Delete folder">
            <button
              type="button"
              className={`${hubStyles.iconBtn} ${viewStyles.fileDeleteBtn}`}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSubfolder(sub);
              }}
              aria-label={`Delete ${sub.name}`}
            >
              <Trash2 size={18} strokeWidth={2} />
            </button>
          </Tooltip>}
          <Tooltip title="Open folder">
            <button
              type="button"
              className={hubStyles.iconBtn}
              onClick={(e) => {
                e.stopPropagation();
                openSubfolder(sub);
              }}
              aria-label={`Open ${sub.name}`}
            >
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </Tooltip>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <PageLoader message="Loading files..." />
      </ThemeProvider>
    );
  }

  if (kgDataset) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className={viewStyles.knowledgeBaseView}>
          <PageHeader
            title={folder?.name || kgDataset.label}
            subtitle="This folder is managed by a Knowledge Graph dataset"
            backButton={<ArrowLeft size={22} strokeWidth={2} />}
            onBackClick={handleBack}
          />
          <div className={hubStyles.listCard}>
            <div className={hubStyles.emptyState}>
              <div className={hubStyles.emptyIcon} aria-hidden>
                <Network size={28} strokeWidth={1.75} />
              </div>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
                Records live in “{kgDataset.label}”
              </p>
              <p style={{ margin: '0 0 20px', maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
                This folder holds the records pushed into that dataset. They are stored as
                searchable chunks rather than files, so a file list here will always be empty —
                and uploading a file would not add a record.
              </p>
              <button
                type="button"
                className={hubStyles.uploadBtn}
                onClick={() => navigate(`/knowledge-graph/${kgDataset.id}?tab=records`)}
              >
                <Network size={18} strokeWidth={2} aria-hidden />
                Open the dataset
              </button>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={viewStyles.knowledgeBaseView}>
        <PageHeader
          title={folder?.name || 'D3 Folder'}
          subtitle="View and manage subfolders and files in this D3 knowledge base folder"
          backButton={<ArrowLeft size={22} strokeWidth={2} />}
          onBackClick={handleBack}
          actionButton={headerActions}
        />

        {/* Breadcrumb path + mirrored badge */}
        {(folderPath.length > 0 || currentMirrored) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 6,
              margin: '0 0 12px',
              color: 'var(--text-secondary)',
              fontSize: '0.82rem',
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/knowledge-hub?tab=d3')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                font: 'inherit',
              }}
            >
              Customer Data
            </button>
            {folderPath.map((node, idx) => {
              const isLast = idx === folderPath.length - 1;
              return (
                <span key={node.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ChevronRight size={14} strokeWidth={2} aria-hidden />
                  {isLast ? (
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{node.name}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(`/knowledge-base-d3/folder/${node.id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        font: 'inherit',
                      }}
                    >
                      {node.name}
                    </button>
                  )}
                </span>
              );
            })}
            {currentMirrored && (
              <Chip
                icon={<Cloud size={13} strokeWidth={2} />}
                label="Synced from Google Drive"
                size="small"
                variant="outlined"
                sx={{
                  ml: 0.5,
                  fontSize: '0.65rem',
                  color: '#8b5cf6',
                  borderColor: '#8b5cf6',
                  '& .MuiChip-icon': { color: '#8b5cf6' },
                }}
              />
            )}
          </div>
        )}

        <div className={viewStyles.filesStatsStrip}>
          {statItems.map((s) => (
            <div key={s.label} className={viewStyles.filesStatCard}>
              <p className={viewStyles.filesStatValue} style={{ color: s.color }}>
                {s.value}
              </p>
              <p className={viewStyles.filesStatLabel}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className={hubStyles.listCard}>
          <div className={hubStyles.folderPanelToolbar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Filter size={18} strokeWidth={2} style={{ color: 'var(--primary)', flexShrink: 0 }} aria-hidden />
              <p className={hubStyles.listTitle} style={{ margin: 0 }}>
                Filters &amp; search
              </p>
            </div>
            <div className={hubStyles.folderToolbarRight}>
              <button
                type="button"
                className={hubStyles.iconBtn}
                onClick={loadData}
                title="Refresh"
                aria-label="Refresh file list"
              >
                <RefreshCw size={18} strokeWidth={2} />
              </button>
            </div>
          </div>
          <div className={viewStyles.filesFiltersBody}>
            <div className={hubStyles.searchWrap}>
              <Search className={hubStyles.searchIcon} size={16} strokeWidth={2} aria-hidden />
              <input
                type="search"
                className={hubStyles.searchInput}
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search files"
              />
            </div>
            <select
              className={viewStyles.filesNativeSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <button
              type="button"
              className={`${hubStyles.viewAgentBtn} ${viewStyles.filesClearBtn}`}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className={hubStyles.listCard}>
          {subfolders.length === 0 && filteredFiles.length === 0 && !loading ? (
            <div className={hubStyles.emptyState}>
              <div className={hubStyles.emptyIcon} aria-hidden>
                <FolderOpen size={28} strokeWidth={1.75} />
              </div>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
                No files found in this D3 folder
              </p>
              <p style={{ margin: '0 0 20px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
                {files.length === 0
                  ? "This D3 folder doesn't contain any files yet. Upload your first file to get started."
                  : 'No files match your current filters. Try adjusting your search criteria.'}
              </p>
              {canModifyFiles && <button type="button" className={hubStyles.uploadBtn} onClick={handleUploadFiles}>
                <Upload size={18} strokeWidth={2} aria-hidden />
                Upload files
              </button>}
            </div>
          ) : (
            <>
              <div className={hubStyles.listHeader}>
                <div className={hubStyles.listHeaderLeft}>
                  <p className={hubStyles.listTitle}>Folder contents</p>
                  <span className={hubStyles.countLabel}>
                    {[
                      subfolders.length > 0
                        ? `${subfolders.length} folder${subfolders.length === 1 ? '' : 's'}`
                        : null,
                      `${filteredFiles.length} file${filteredFiles.length === 1 ? '' : 's'}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              </div>

              {/* Folders first, then files — like a file explorer. */}
              {subfolders.map(renderSubfolderRow)}

              {filteredFiles.length === 0 ? (
                <div
                  style={{
                    padding: '16px 18px',
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem',
                  }}
                >
                  {files.length === 0
                    ? 'No files in this folder yet.'
                    : 'No files match your current filters.'}
                </div>
              ) : (
                paginatedFiles.map((file) => {
                const proc = file.processing_status;
                return (
                  <div key={file.id} className={hubStyles.fileRow} style={{ cursor: 'default' }}>
                    <div className={hubStyles.fileRowMain}>
                      <div className={`${hubStyles.typeIconWrap} ${hubStyles.typeDoc}`}>
                        <FileText size={20} strokeWidth={2} aria-hidden />
                      </div>
                      <div className={hubStyles.fileMeta}>
                        <h3>{file.file_name}</h3>
                        <p className="desc">ID: {file.file_id}</p>
                        <div className="subRow">
                          <span className="tag">{formatFileSize(file.file_size)}</span>
                          <span>·</span>
                          <span>{formatDate(file.created_at)}</span>
                        </div>
                        {proc === 'failed' && file.error_message && (
                          <p
                            className="desc"
                            style={{ color: '#dc2626', marginTop: 4, whiteSpace: 'normal' }}
                            title={file.error_message}
                          >
                            {file.error_message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={viewStyles.fileRowBlock}>
                      <div className={viewStyles.fileRowMetaLine}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {getStatusIcon(proc)}
                          <span className={`${hubStyles.folderChip} ${getStatusChipClass(proc)}`}>
                            {proc}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className={`${hubStyles.rowActions} ${viewStyles.fileFolderRowActions}`}>
                      <Tooltip title="View details">
                        <button
                          type="button"
                          className={hubStyles.iconBtn}
                          onClick={() => handleViewDetails(file)}
                          aria-label="View file details"
                        >
                          <Eye size={18} strokeWidth={2} />
                        </button>
                      </Tooltip>
                      {canModifyFiles && proc === 'failed' && (
                        <Tooltip title="Retry processing">
                          <button
                            type="button"
                            className={hubStyles.iconBtn}
                            onClick={() => handleRetryFile(file.id)}
                            aria-label="Retry processing"
                          >
                            <RefreshCw size={18} strokeWidth={2} />
                          </button>
                        </Tooltip>
                      )}
                      {canModifyFiles && <Tooltip title="Delete file">
                        <button
                          type="button"
                          className={`${hubStyles.iconBtn} ${viewStyles.fileDeleteBtn}`}
                          onClick={() => handleDeleteClick(file)}
                          aria-label="Delete file"
                        >
                          <Trash2 size={18} strokeWidth={2} />
                        </button>
                      </Tooltip>}
                    </div>
                  </div>
                );
                })
              )}
              {filteredFiles.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredFiles.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              )}
            </>
          )}
        </div>

        <Dialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">D3 File Details</Typography>
              <IconButton onClick={() => setDetailsDialogOpen(false)} size="small" aria-label="Close">
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent>
            {selectedFile && (
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Basic Information
                    </Typography>
                    <div style={{ marginBottom: 16 }}>
                      <Typography variant="body2">
                        <strong>File Name:</strong> {selectedFile.file_name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>File ID:</strong> {selectedFile.file_id}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Size:</strong> {formatFileSize(selectedFile.file_size)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Created:</strong> {formatDate(selectedFile.created_at)}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Processing Status
                    </Typography>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        {getStatusIcon(selectedFile.processing_status)}
                        <Typography variant="body2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                          <strong>Status:</strong> {selectedFile.processing_status}
                        </Typography>
                      </div>
                      {selectedFile.processing_started_at && (
                        <Typography variant="body2">
                          <strong>Started:</strong> {formatDate(selectedFile.processing_started_at)}
                        </Typography>
                      )}
                      {selectedFile.processing_completed_at && (
                        <Typography variant="body2">
                          <strong>Completed:</strong> {formatDate(selectedFile.processing_completed_at)}
                        </Typography>
                      )}
                      {selectedFile.error_message && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {selectedFile.error_message}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>


        <FileDeleteDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setFileToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          file={fileToDelete}
          isDeleting={isDeleting}
        />
        <ResourceShareDialog
          open={shareOpen}
          resource={folder ? { type: 'folder_dim3', id: folderId, name: folder.name } : null}
          onClose={() => setShareOpen(false)}
          onSuccess={(result) => {
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            showToast(warnings.length ? `Folder shared. ${warnings.join(' ')}` : 'Folder shared successfully.', warnings.length ? 'warning' : 'success');
          }}
        />
        <ManageSharingDialog
          open={manageSharingOpen}
          resource={folder ? { type: 'folder_dim3', id: folderId, name: folder.name } : null}
          onClose={() => setManageSharingOpen(false)}
        />

        <FolderDeleteDialog
          open={folderDeleteDialog.open}
          onClose={handleCancelDeleteSubfolder}
          onConfirm={handleConfirmDeleteSubfolder}
          folder={folderDeleteDialog.folder}
          subfolderCount={folderDeleteDialog.subfolderCount}
          isDeleting={folderDeleteDialog.isDeleting}
        />

        <GoogleDriveSyncDialog
          open={driveSyncOpen}
          onClose={() => setDriveSyncOpen(false)}
          dim3FolderId={folderId}
          dim3FolderName={folder?.name || ''}
          onSuccess={() => {
            showToast('Google Drive sync started.', 'success');
          }}
        />

        {/* Toast Notifications */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
};

export default FolderFilesViewD3;
